import app from 'flarum/forum/app';
import Select from 'flarum/common/components/Select';
import Icon from 'flarum/common/components/Icon';
import Stream from 'flarum/common/utils/Stream';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Switch from 'flarum/common/components/Switch';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
import { field, section } from './Field';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.' + key, params);
const lib = (key, params) => app.translator.trans('ppk2-profile.lib.team.' + key, params);

/** Suffix added to the main nick in the game playlog, by team role. */
export const ROLE_SUFFIX = {
  captain: ' (C)',
  assistant: ' (A)'
};

/**
 * Team part of the modal state. `initial` is the ppk2Team attribute:
 * { id, name, teamStatus, role, status, captainLocked } or null.
 */
export function teamState(initial) {
  return {
    initial: initial || null,
    teams: null,
    // GET /api/ppk2/teams
    loadError: false,
    showErrors: false,
    inTeam: Stream(!!initial),
    choice: Stream(initial ? 't' + initial.id : ''),
    // last captain leaving / switching: «Удалить команду или оставить пустой?»
    prompt: false,
    deleteChoice: null,
    // 'delete' | 'keep'
    onAnswer: null,
    // set by ProfileModal: continue saving after the answer
    leavePrompt: false,
    // «Выйти из команды» by the last captain -> «Удалить или оставить пустой?»
    leaving: false
  };
}

/** A confirmed membership (incl. captain / зам) is fixed: switch and team list are disabled. */
export function teamLocked(ts) {
  return !!ts.initial && ts.initial.status === 'confirmed';
}

/** «Выйти из команды»: immediate POST /api/ppk2/team/leave; the main team only (mini-teams stay). */
function leaveNow(ts, user, del) {
  ts.leaving = true;
  m.redraw();
  return app.request({
    method: 'POST',
    url: app.forum.attribute('apiUrl') + '/ppk2/team/leave',
    body: {
      delete: !!del
    }
  }).then(response => {
    const r = response.result || {};
    ts.initial = null;
    ts.inTeam(false);
    ts.choice('');
    ts.prompt = false;
    ts.deleteChoice = null;
    ts.leavePrompt = false;
    ts.bannerUrl = null;
    ts.showBanner(true);
    user.pushAttributes({
      ppk2Team: null
    });
    app.alerts.show({
      type: 'success'
    }, r.deleted ? t('club_team.team.deleted', {
      name: r.name
    }) : t('club_team.team.left', {
      name: r.name
    }));
  }).catch(() => {}).finally(() => {
    ts.leaving = false;
    m.redraw();
  });
}
function leaveButton(ts, user, loading) {
  const init = ts.initial;
  if (!init) return null;
  const blocked = !!init.captainLocked;
  return m("div", {
    className: "Ppk2LeaveTeam"
  }, m(Button, {
    className: "Button Ppk2LeaveTeam-button Ppk2IconOnPhone",
    title: t('club_team.team.leave'),
    "aria-label": t('club_team.team.leave'),
    icon: "fas fa-right-from-bracket",
    disabled: loading || blocked || ts.leaving,
    loading: ts.leaving,
    onclick: () => {
      if (!window.confirm(extractText(t('club_team.team.leave_confirm', {
        name: init.name
      })))) return;
      if (init.lastMember && init.role === 'captain') ts.leavePrompt = true;else leaveNow(ts, user, false);
    }
  }, t('club_team.team.leave')), blocked ? m("div", {
    className: "Ppk2LeaveTeam-hint"
  }, t('club_team.team.leave_blocked')) : null);
}
function leavePrompt(ts, user) {
  if (!ts.leavePrompt || !ts.initial) return null;
  return m("div", {
    className: "Ppk2Prompt"
  }, m("div", {
    className: "Ppk2Prompt-text"
  }, m(Icon, {
    name: "fas fa-circle-question"
  }), " ", t('club_team.team.last_prompt', {
    name: ts.initial.name
  })), m("div", {
    className: "Ppk2Prompt-buttons"
  }, m("button", {
    type: "button",
    className: "Button Ppk2Prompt-delete",
    disabled: ts.leaving,
    onclick: () => leaveNow(ts, user, true)
  }, m(Icon, {
    name: "fas fa-trash"
  }), " ", t('club_team.team.delete')), m("button", {
    type: "button",
    className: "Button",
    disabled: ts.leaving,
    onclick: () => leaveNow(ts, user, false)
  }, t('club_team.team.keep_empty')), m("button", {
    type: "button",
    className: "Button Button--link",
    onclick: () => ts.leavePrompt = false
  }, t('buttons.cancel'))));
}

/** The saved team will be left by its captain who is its last member -> ask before saving. */
export function needsDeletePrompt(ts) {
  const init = ts.initial;
  return !!init && !!init.lastMember && init.role === 'captain' && teamPayload(ts) !== undefined;
}
function deletePrompt(ts) {
  if (!ts.prompt || !needsDeletePrompt(ts)) return null;
  const answer = choice => {
    ts.deleteChoice = choice;
    ts.prompt = false;
    if (ts.onAnswer) ts.onAnswer();
  };
  return m("div", {
    className: "Ppk2Prompt"
  }, m("div", {
    className: "Ppk2Prompt-text"
  }, m(Icon, {
    name: "fas fa-circle-question"
  }), " ", t('club_team.team.last_prompt', {
    name: ts.initial.name
  })), m("div", {
    className: "Ppk2Prompt-buttons"
  }, m("button", {
    type: "button",
    className: "Button Ppk2Prompt-delete",
    onclick: () => answer('delete')
  }, m(Icon, {
    name: "fas fa-trash"
  }), " ", t('club_team.team.delete')), m("button", {
    type: "button",
    className: "Button",
    onclick: () => answer('keep')
  }, t('club_team.team.keep_empty')), m("button", {
    type: "button",
    className: "Button Button--link",
    onclick: () => ts.prompt = false
  }, t('buttons.cancel'))));
}
export function loadTeams(ts) {
  return app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/ppk2/teams'
  }).then(response => {
    ts.teams = response.teams || [];
    if (ts.initial && !ts.teams.some(x => x.id === ts.initial.id)) {
      ts.teams.push({
        id: ts.initial.id,
        name: ts.initial.name,
        status: ts.initial.teamStatus
      });
    }
  }).catch(() => {
    ts.loadError = true;
  }).finally(() => m.redraw());
}

/** Value for PATCH (ppk2Team) or `undefined` when nothing changed (not sent). */
export function teamPayload(ts) {
  const init = ts.initial;
  if (!ts.inTeam()) return init ? null : undefined;
  if (!ts.choice()) return undefined; // nothing chosen yet (teamLocalError reports it)
  const id = parseInt(ts.choice().substr(1), 10);
  if (init && init.id === id) return undefined;
  return {
    teamId: id
  };
}

/** [message] of a problem that blocks saving, or null. `all` also returns «choose a team». */
export function teamLocalError(ts, all = true) {
  const init = ts.initial;
  if (init && init.captainLocked && teamPayload(ts) !== undefined) return lib('captain_locked', {
    team: init.name
  });
  if (all && ts.inTeam() && !ts.choice()) return lib('required');
  return null;
}

/** Role / status as they will be after saving (a new or changed team -> player, needs confirmation). */
function effective(ts) {
  const unchanged = ts.initial && teamPayload(ts) === undefined && ts.inTeam();
  return {
    role: unchanged ? ts.initial.role : 'player',
    status: unchanged ? ts.initial.status : 'needs_confirmation'
  };
}
function errorLine(text) {
  return text ? m("div", {
    className: "Ppk2NickStatus Ppk2NickStatus--invalid"
  }, m(Icon, {
    name: "fas fa-circle-xmark"
  }), " ", text) : null;
}
function roleBadge(role) {
  if (role === 'captain') {
    return m("span", {
      className: "Ppk2RoleBadge Ppk2RoleBadge--captain"
    }, m(Icon, {
      name: "fas fa-crown"
    }), " ", t('club_team.team_role.captain'));
  }
  if (role === 'assistant') {
    return m("span", {
      className: "Ppk2RoleBadge Ppk2RoleBadge--assistant"
    }, m(Icon, {
      name: "fas fa-star"
    }), " ", t('club_team.team_role.assistant'));
  }
  return null;
}
function statusBadge(status) {
  const ok = status === 'confirmed';
  return m("span", {
    className: 'Ppk2StatusBadge Ppk2StatusBadge--' + (ok ? 'verified' : 'needs'),
    title: t(ok ? 'status.confirmed' : 'status.needs_confirmation')
  }, m(Icon, {
    name: ok ? 'fas fa-circle-check' : 'fas fa-clock'
  }), " ", m("span", {
    className: "Ppk2StatusBadge-text"
  }, t(ok ? 'status.confirmed' : 'status.needs_confirmation')));
}

/** Rows of the «КОМАНДЫ» section. */
export function teamSection(ts, user, loading) {
  const captainError = teamLocalError(ts, false);
  const eff = effective(ts);
  const rows = [section(t('club_team.section_teams'))];
  rows.push(field('inTeam', t('club_team.in_team.label'), [m("div", {
    className: "Ppk2Switch",
    "data-name": "ppk2InTeam"
  }, m(Switch, {
    state: ts.inTeam(),
    disabled: loading || teamLocked(ts) || ts.leaving,
    onchange: value => ts.inTeam(value)
  }, t('club_team.in_team.checkbox'))), !ts.inTeam() ? errorLine(captainError) : null, !ts.inTeam() ? deletePrompt(ts) : null, leavePrompt(ts, user)], leaveButton(ts, user, loading), t('club_team.in_team.tip')));
  if (!ts.inTeam()) return rows;
  let body;
  if (ts.loadError) body = m("div", {
    className: "Ppk2NickStatus Ppk2NickStatus--invalid"
  }, t('club_team.team.load_error'));else if (!ts.teams) body = m(LoadingIndicator, {
    display: "inline",
    size: "small"
  });else if (!ts.teams.length) body = m("div", {
    className: "Ppk2Muted"
  }, t('club_team.team.none_available'));else {
    // The placeholder stays in the list: removing it while the <select> is open
    // makes the browser show the wrong option after Mithril re-renders.
    const options = {
      '': {
        label: t('club_team.team.choose'),
        disabled: !!ts.choice()
      }
    };
    ts.teams.forEach(x => options['t' + x.id] = x.name);
    const required = ts.showErrors && !ts.choice() ? lib('required') : null;
    body = [m("div", {
      className: 'Ppk2TeamSelect' + (roleBadge(eff.role) ? ' Ppk2TeamSelect--badge' : '')
    }, m(Select, {
      className: "Ppk2TeamSelect-input",
      name: "ppk2Team",
      options: options,
      value: ts.choice(),
      disabled: loading || teamLocked(ts) || ts.leaving,
      onchange: v => ts.choice(v)
    }), ts.choice() ? roleBadge(eff.role) : null), errorLine(captainError || required), deletePrompt(ts)];
  }
  rows.push(field('team', t('club_team.team.label'), body, ts.choice() ? statusBadge(eff.status) : null, t('club_team.team.tip')));
  const mainNick = user.attribute('ppk2MainNick');
  rows.push(field('playlog', t('club_team.playlog.label'), m("div", {
    className: 'Ppk2ReadonlyValue' + (mainNick ? '' : ' Ppk2ReadonlyValue--muted')
  }, mainNick ? mainNick + (ts.choice() ? ROLE_SUFFIX[eff.role] || '' : '') : t('club_team.playlog.no_main_nick')), null, t('club_team.playlog.tip')));
  return rows;
}
flarum.reg.add('ppk2-profile', 'forum/components/TeamSection', { ROLE_SUFFIX: ROLE_SUFFIX,teamState: teamState,teamLocked: teamLocked,needsDeletePrompt: needsDeletePrompt,loadTeams: loadTeams,teamPayload: teamPayload,teamLocalError: teamLocalError,teamSection: teamSection, });